/**
 * Utility functions for generating and managing auto-populated descriptions
 * in quotation line items based on helper function inputs
 */

/**
 * Generate a service-only description based on selected services
 * @param {Object} services - Services object with transportation, assembly, endorsement
 * @returns {string} Generated service description
 */
export const generateServiceOnlyDescription = (services = {}) => {
  const serviceLines = [];
  
  if (services.transportation) {
    serviceLines.push('- 2 way transport (delivery and collection)');
  }
  
  if (services.assembly) {
    serviceLines.push('- Installation and dismantling');
    serviceLines.push('- Safe-to-use tag (1 week validity)');
  }
  
  if (services.endorsement) {
    serviceLines.push('- Weekly inspection and endorsement');
  }
  
  return serviceLines.join('\n');
};

/**
 * Generate a detailed description based on scaffolding parameters
 * @param {string} width - 'single' or 'double'
 * @param {number} workingHeight - Height in meters
 * @param {number} quantity - Quantity of items
 * @param {string} customPrefix - Optional custom prefix
 * @param {Object} services - Optional services for per-item services
 * @returns {string} Generated description
 */
export const generateScaffoldingDescription = (width, workingHeight, quantity = 1, customPrefix = '', services = null) => {
  const descriptionLines = [];
  
  // Check if any services are selected
  const hasServices = services && (services.transportation || services.assembly || services.endorsement);
  
  // If working height is provided, generate scaffolding description
  if (workingHeight && workingHeight > 0) {
    // Generate detailed multi-line description
    const widthText = width === 'double' ? 'Double Width' : 'Single Width';
    const prefix = customPrefix ? `${customPrefix} - ` : '';
    
    // Calculate dimensions with 2 decimal places
    const standardLength = 2.00;
    const widthMeasurement = width === 'double' ? 1.35 : 0.80;
    const towerHeight = workingHeight - 1;
    const platformHeight = workingHeight - 2;
    
    // Format all measurements to 2 decimal places
    const formattedLength = standardLength.toFixed(2);
    const formattedWidth = widthMeasurement.toFixed(2);
    const formattedTowerHeight = towerHeight.toFixed(2);
    const formattedPlatformHeight = platformHeight.toFixed(2);
    const formattedWorkingHeight = workingHeight.toFixed(2);
    
    // Add prefix if specified
    if (prefix) {
      descriptionLines.push(`${prefix}${widthText} Aluminium Mobile Scaffold`);
    } else {
      descriptionLines.push(`${widthText} Aluminium Mobile Scaffold`);
    }
    
    // Add dimensions
    descriptionLines.push(`Dimensions: ${formattedLength}mL x ${formattedWidth}mW x ${formattedTowerHeight}mH`);
    
    // Add height specifications
    descriptionLines.push(`Platform Height: ${formattedPlatformHeight}m`);
    descriptionLines.push(`Tower Height: ${formattedTowerHeight}m`);
    descriptionLines.push(`Max Working Height: ${formattedWorkingHeight}m`);
  } else if (hasServices) {
    // If no working height but services are selected, generate service-only description
    return generateServiceOnlyDescription(services);
  } else if (width) {
    // If only width is specified (no working height), generate basic scaffolding description
    const widthText = width === 'double' ? 'Double Width' : 'Single Width';
    const prefix = customPrefix ? `${customPrefix} - ` : '';
    
    if (prefix) {
      descriptionLines.push(`${prefix}${widthText} Aluminium Mobile Scaffold`);
    } else {
      descriptionLines.push(`${widthText} Aluminium Mobile Scaffold`);
    }
    descriptionLines.push('Dimensions: TBC');
  } else {
    // Fallback: generate generic scaffolding description
    descriptionLines.push('Aluminium Mobile Scaffold');
    descriptionLines.push('Dimensions: TBC');
  }
  
  // Add services if provided and we have scaffolding product
  if (services && descriptionLines.length > 0) {
    const serviceLines = [];
    
    if (services.transportation) {
      serviceLines.push('- 2 way transport (delivery and collection)');
    }
    
    if (services.assembly) {
      serviceLines.push('- Installation and dismantling');
      serviceLines.push('- Safe-to-use tag (1 week validity)');
    }
    
    if (services.endorsement) {
      serviceLines.push('- Weekly inspection and endorsement');
    }
    
    // Add services to description
    if (serviceLines.length > 0) {
      if (descriptionLines.length > 0) {
        descriptionLines.push(''); // Add empty line separator
      }
      descriptionLines.push(...serviceLines);
    }
  }
  
  return descriptionLines.join('\n');
};

/**
 * Check if two sets of parameters would generate the same description
 * @param {Object} params1 - First set of parameters
 * @param {Object} params2 - Second set of parameters
 * @returns {boolean} True if parameters are equivalent
 */
export const areDescriptionParamsEqual = (params1, params2) => {
  if (!params1 || !params2) return false;
  
  return (
    params1.width === params2.width &&
    params1.workingHeight === params2.workingHeight &&
    params1.quantity === params2.quantity
  );
};

/**
 * Extract parameters from a line item for description generation
 * @param {Object} lineItem - Line item object
 * @returns {Object} Parameters object
 */
export const extractDescriptionParams = (lineItem) => {
  return {
    width: lineItem.width, // Don't default to 'single' - allow undefined/null
    workingHeight: lineItem.workingHeight,
    quantity: lineItem.quantity || 1
  };
};

/**
 * Check if a description appears to be auto-generated
 * @param {string} description - Description to check
 * @returns {boolean} True if description looks auto-generated
 */
export const isAutoGeneratedDescription = (description) => {
  if (!description) return true;
  
  // Check for common auto-generated patterns
  const autoPatterns = [
    // Legacy patterns
    /^\d*x?\s*(Single|Double)\s*Width\s*Scaffolding\s*Tower/i,
    /Scaffolding\s*Tower.*\d+m\s*Working\s*Height/i,
    // New detailed patterns - removed quantity patterns
    /^(Single|Double)\s*Width\s*Aluminium\s*Mobile\s*Scaffold/i,
    /Dimensions:\s*\d+\.\d+mL\s*x\s*\d+\.\d+mW\s*x\s*\d+\.\d+mH/i,
    /Platform\s*Height:\s*\d+\.\d+m/i,
    /Tower\s*Height:\s*\d+\.\d+m/i,
    /Max\s*Working\s*Height:\s*\d+\.\d+m/i,
    // Service-only patterns
    /^-\s*2\s*way\s*transport/i,
    /^-\s*Installation\s*and\s*dismantling/i,
    /^-\s*Weekly\s*inspection\s*and\s*endorsement/i,
    /Safe-to-use\s*tag/i,
    // Fallback patterns
    /^Aluminium\s*Mobile\s*Scaffold$/i,
    /Dimensions:\s*TBC/i,
    /^(Single|Double)\s*Width\s*Aluminium\s*Mobile\s*Scaffold\s*Dimensions:\s*TBC/i
  ];
  
  return autoPatterns.some(pattern => pattern.test(description));
};

/**
 * Enhanced line item structure for tracking auto-generation state
 */
export const createEnhancedLineItem = (baseItem = {}) => {
  const params = extractDescriptionParams(baseItem);
  const services = baseItem.services || {
    transportation: false,
    assembly: false,
    endorsement: false
  };
  
  const autoDescription = generateScaffoldingDescription(
    params.width,
    params.workingHeight,
    params.quantity,
    '',
    services
  );
  
  return {
    id: baseItem.id || Date.now(),
    description: baseItem.description || autoDescription,
    quantity: baseItem.quantity || 1,
    unitPrice: baseItem.unitPrice || 0,
    total: (baseItem.quantity || 1) * (baseItem.unitPrice || 0),
    workingHeight: baseItem.workingHeight,
    width: baseItem.width, // Don't default to 'single' - allow undefined/null
    
    // Per-item services
    services: services,
    
    // Enhanced tracking properties
    autoDescription: autoDescription,
    isManuallyEdited: baseItem.description ? !isAutoGeneratedDescription(baseItem.description) : false,
    lastAutoParams: params,
    showUpdatePrompt: false
  };
};

/**
 * Update line item with new parameters and smart description handling
 * @param {Object} lineItem - Current line item
 * @param {string} field - Field being updated
 * @param {any} value - New value
 * @returns {Object} Updated line item
 */
export const updateLineItemWithSmartDescription = (lineItem, field, value) => {
  const updatedItem = { ...lineItem, [field]: value };
  
  // Recalculate total if quantity or unitPrice changed
  if (field === 'quantity' || field === 'unitPrice') {
    updatedItem.total = (updatedItem.quantity || 0) * (updatedItem.unitPrice || 0);
  }
  
  // Handle service updates
  if (field === 'services') {
    updatedItem.services = { ...lineItem.services, ...value };
  }
  
  // Handle description updates for helper function changes (including services)
  if (field === 'width' || field === 'workingHeight' || field === 'quantity' || field === 'services') {
    const newParams = extractDescriptionParams(updatedItem);
    const newAutoDescription = generateScaffoldingDescription(
      newParams.width,
      newParams.workingHeight,
      newParams.quantity,
      '',
      updatedItem.services
    );
    
    // Update auto description
    updatedItem.autoDescription = newAutoDescription;
    
    // If not manually edited, update description automatically
    if (!updatedItem.isManuallyEdited) {
      updatedItem.description = newAutoDescription;
    } else {
      // If manually edited and params changed, show update prompt
      const paramsChanged = !areDescriptionParamsEqual(updatedItem.lastAutoParams, newParams) || field === 'services';
      if (paramsChanged) {
        updatedItem.showUpdatePrompt = true;
      }
    }
    
    updatedItem.lastAutoParams = newParams;
  }
  
  // Handle manual description edits
  if (field === 'description') {
    updatedItem.isManuallyEdited = true;
    updatedItem.showUpdatePrompt = false;
  }
  
  return updatedItem;
};

/**
 * Reset line item description to auto-generated version
 * @param {Object} lineItem - Line item to reset
 * @returns {Object} Updated line item
 */
export const resetToAutoDescription = (lineItem) => {
  return {
    ...lineItem,
    description: lineItem.autoDescription,
    isManuallyEdited: false,
    showUpdatePrompt: false
  };
};

/**
 * Regenerate auto descriptions for line items on form load
 * This ensures descriptions are populated based on helper function values
 * even when the form loads with existing data
 * @param {Array} lineItems - Array of line items
 * @returns {Array} Updated line items with regenerated auto descriptions
 */
export const regenerateAutoDescriptionsOnLoad = (lineItems) => {
  return lineItems.map(item => {
    const params = extractDescriptionParams(item);
    
    // Ensure services exist on the item
    const services = item.services || {
      transportation: false,
      assembly: false,
      endorsement: false
    };
    
    const newAutoDescription = generateScaffoldingDescription(
      params.width,
      params.workingHeight,
      params.quantity,
      '',
      services
    );
    
    // If description is empty or appears to be auto-generated, update it
    const shouldAutoFill = !item.description || 
                          item.description.trim() === '' || 
                          (!item.isManuallyEdited && isAutoGeneratedDescription(item.description));
    
    return {
      ...item,
      services: services, // Ensure services are included
      autoDescription: newAutoDescription,
      description: shouldAutoFill ? newAutoDescription : item.description,
      isManuallyEdited: shouldAutoFill ? false : (item.isManuallyEdited || false),
      lastAutoParams: params,
      showUpdatePrompt: false
    };
  });
};
